import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { forgotPasswordRequestSchema, type ForgotPasswordRequest } from "@nh/shared";
import {
  Alert,
  AuthCard,
  AuthLayout,
  Button,
  EmailField,
  Form,
  FormError,
  SubmitButton,
} from "@/shared/ui/core";
import { useCountdown } from "@/shared/lib/useCountdown";
import { forgotPasswordRequest } from "./auth.api";
import { RESEND_COOLDOWN_SECONDS, waitLabel } from "./auth.cooldown";
import { AuthSwitchLink } from "./components/AuthSwitchLink";
import { useAuthSubmit } from "./useAuthSubmit";

/**
 * El mensaje de confirmación. Es UNO SOLO, y es deliberado.
 *
 * ⚠️ NO lo cambies por "te enviamos un correo" ni por "no encontramos esa
 * cuenta". Este texto es idéntico exista o no el correo porque el servidor
 * responde idéntico en los dos casos (ver `forgotPasswordResponseSchema`).
 * Si esta pantalla distinguiera los dos casos, cualquiera podría escribir
 * correos uno por uno y usarla como buscador de quién tiene cuenta — y en
 * un negocio de red, la lista de quién está dentro es justo lo que querría
 * un competidor.
 *
 * El "Si ese correo tiene una cuenta" no es timidez de redacción: es la
 * única frase honesta que se puede decir sin saber —ni poder saber— cuál
 * de los dos casos ocurrió.
 */
const CONFIRMATION_MESSAGE =
  "Si ese correo tiene una cuenta, te enviamos un enlace para crear una " +
  "contraseña nueva. Revisa tu bandeja y el correo no deseado.";

/**
 * "¿Olvidaste tu contraseña?" — pedir el enlace para crear una nueva.
 *
 * ── Una card, dos estados ───────────────────────────────────────────
 * Es la MISMA `AuthCard` del login y del registro, y no dos pantallas:
 * pedir el enlace y confirmar que se pidió son dos momentos de la misma
 * tarea. Navegar a otra ruta al enviar rompería el botón de atrás —
 * volvería a un formulario ya enviado— y perdería el correo que hay que
 * enseñar en la confirmación.
 *
 * ── El esquema no es opcional ───────────────────────────────────────
 * `EmailField` declara tipo, teclado y autocompletado; NO valida nada. Y
 * `<Form>` pinta `noValidate` a propósito, así que el navegador tampoco
 * opina. Quien valida es `forgotPasswordRequestSchema` —el mismo que usa
 * el servidor— enchufado con `zodResolver`. De paso normaliza: el correo
 * llega al API en minúsculas y sin espacios, de modo que
 * "  Anahi@ITB.com " y "anahi@itb.com" no son dos cuentas distintas.
 */
export function ForgotPasswordPage() {
  const form = useForm<ForgotPasswordRequest>({
    resolver: zodResolver(forgotPasswordRequestSchema),
    defaultValues: { email: "" },
    mode: "onSubmit",
  });

  /**
   * El correo YA NORMALIZADO al que se pidió el enlace, o `null` mientras
   * no se ha pedido ninguno. Es lo que decide cuál de los dos estados se
   * pinta, y es lo que se enseña en la confirmación: quien se equivocó de
   * letra necesita VERLO para darse cuenta.
   */
  const [sentTo, setSentTo] = useState<string | null>(null);

  const cooldown = useCountdown();

  const { submit, formError, dismissFormError } = useAuthSubmit({
    form,
    // Un fallo de validación que el servidor señale sobre `email` sí
    // pertenece al campo (un correo malformado que se coló). Lo que el
    // servidor NUNCA manda es "esa cuenta no existe": no hay código para
    // eso, a propósito.
    fields: ["email"],
    action: async (values) => {
      await forgotPasswordRequest(values);
      setSentTo(values.email);
    },
    onSuccess: () => cooldown.start(RESEND_COOLDOWN_SECONDS),
  });

  /**
   * Reenviar es EXACTAMENTE el mismo envío: `handleSubmit` vuelve a
   * validar el correo que sigue en el formulario y repite la llamada.
   *
   * Los campos están desmontados en la confirmación, pero react-hook-form
   * conserva los valores de lo desmontado, así que no hace falta guardar
   * el correo por duplicado ni pasarlo a mano.
   */
  const resend = () => {
    void form.handleSubmit(submit)();
  };

  /**
   * Volver al formulario CONSERVANDO el correo escrito.
   *
   * Casi siempre se llega aquí por una letra mal puesta, y borrar el campo
   * obligaría a escribirlo entero otra vez para corregir un carácter.
   *
   * La espera NO se reinicia a propósito: si volver al formulario la
   * cancelara, "otro correo" + enviar sería el atajo para saltársela con
   * el mismo correo de antes.
   */
  const useAnotherEmail = () => {
    dismissFormError();
    setSentTo(null);
  };

  const waiting = cooldown.isRunning;

  return (
    // `sm`, no `md`: aquí hay UN campo. Con el ancho del login la card
    // quedaba con una casilla de correo enorme y aire de sobra a los lados.
    <AuthLayout width="sm">
      <AuthCard
        title={sentTo ? "Revisa tu correo" : "¿Olvidaste tu contraseña?"}
        subtitle={
          sentTo ? (
            <>
              Escribiste <span className="font-medium text-ds-ink">{sentTo}</span>
            </>
          ) : (
            "Escribe tu correo y te mandamos un enlace para crear una contraseña nueva."
          )
        }
        footer={
          <AuthSwitchLink
            prompt="¿Ya la recordaste?"
            linkLabel="Inicio de sesión"
            to="/login"
          />
        }
      >
        {/* Fuera del `<Form>`, igual que en el login: no es un campo, es
            el resultado del último envío — y sirve a los dos estados,
            porque el reenvío puede fallar igual que el primer envío. */}
        <FormError error={formError} onDismiss={dismissFormError} />

        {sentTo ? (
          <div className="grid gap-3">
            <Alert variant="success">{CONFIRMATION_MESSAGE}</Alert>

            <Button
              variant="primary"
              size="lg"
              shape="rounded"
              fullWidth
              disabled={waiting}
              loading={form.formState.isSubmitting}
              onClick={resend}
            >
              {waiting ? waitLabel(cooldown.remaining) : "Reenviar el enlace"}
            </Button>

            <Button
              variant="ghost"
              size="lg"
              shape="rounded"
              fullWidth
              onClick={useAnotherEmail}
            >
              Usar otro correo
            </Button>
          </div>
        ) : (
          <Form form={form} onSubmit={submit} className="grid gap-4">
            <EmailField purpose="account" autoFocus />
            <SubmitButton size="lg" shape="rounded" fullWidth disabled={waiting}>
              {waiting ? waitLabel(cooldown.remaining) : "Enviar el enlace"}
            </SubmitButton>
          </Form>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
